import * as React from 'react';
import { ReactNode } from 'react';
import {
    Control,
    useFieldArray,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { AiAgentV2Data, currentColumnsToReturn, SkillType } from '../../../../../types/AiAgentV2';
import RenderFormFieldsComponent, {
    GroupTypes,
} from '../../../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Card, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { SkillForm } from './AgentSkillsComponent';
import { RadioGroup } from '@mui/material';
import { Label } from 'src/components/ui/label';
import { cn } from 'src/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { EnumSkillRetrieverFields } from 'src/types/Common';
import SkillKeyValueConfigComponent from './SkillKeyValueConfigComponent';
import { useTranslate } from 'sbx-crm-menu-ui';
import { Button } from '../../../../ui/button';
import { Eye } from 'lucide-react';

type Props = {
    control: Control<SkillForm>;
    name?: string;
    register: UseFormRegister<SkillForm>;
    customRetrieverFields: (name: string, group?: string) => ReactNode;
    skillRetrieverFields: GroupTypes<keyof AiAgentV2Data>;
    structured?: boolean;
    dataScience?: boolean;
    watch?: UseFormWatch<SkillForm>;
    getValues?: UseFormGetValues<SkillForm>;
    setValues?: UseFormSetValue<SkillForm>;
    selectedSource: EnumSkillRetrieverFields;
    setSelectedSource: (select: EnumSkillRetrieverFields) => void;
    unregisterUnusedRetrieverFields?: (field: EnumSkillRetrieverFields) => void;
    currentColumnsToReturn: currentColumnsToReturn;
    setCurrentColumnsToReturn: (columnsToReturn: currentColumnsToReturn) => void;
};

const SkillRetrieverComponentDataSourceVersion = ({
    control,
    skillRetrieverFields,
    customRetrieverFields,
    register,
    name = 'config',
    selectedSource,
    setSelectedSource,
    unregisterUnusedRetrieverFields,
    watch,
    currentColumnsToReturn,
    setCurrentColumnsToReturn,
    setValues,
    dataScience,
}: Props) => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');

    const columnsToReturn = useFieldArray({
        name: `new_skill.${name}.columns_to_return` as any,
        control,
    });

    const columnsDescription = useFieldArray({
        name: `new_skill.${name}.columns_description` as any,
        control,
    });

    const renderGroupFields = () => {
        return (
            <SkillKeyValueConfigComponent
                label={agent('columns_to_return')}
                placeholderKey={common('name')}
                placeholderValue={common('description')}
                append={() => {
                    if (dataScience) {
                        columnsDescription.append({
                            name: '',
                            value: '',
                        });
                    } else {
                        columnsToReturn.append({
                            name: '',
                            value: '',
                        });
                    }
                }}
                config={name}
                name={dataScience ? `columns_description` : `columns_to_return`}
                buttonLabel={`${common('add')} ${agent('columns_to_return')}`}
                formArray={dataScience ? columnsDescription : columnsToReturn}
                register={register}
                onchange={() => unregisterUnusedRetrieverFields && unregisterUnusedRetrieverFields(selectedSource)}
            />
        );
    };

    const openNewWindownReport = (key_report: string) => {
        const url = `/analytics/crm-reports/${key_report}`;
        window.open(url, '_blank');
    };

    return (
        <>
            <div className="min-h-screen">
                <div className="w-full max-w-4xl mx-auto p-4 space-y-8">
                    <RadioGroup
                        value={selectedSource}
                        className="grid-cols-1 md:grid-cols-3 gap-4"
                        style={{ display: 'grid' }}
                    >
                        {Object.keys(skillRetrieverFields).map((group) => {
                            const currentGroup = skillRetrieverFields[group];
                            if (
                                currentGroup.label === SkillType.SkillStructuredRetriever ||
                                currentGroup.label === SkillType.SkillDataAnalysis
                            ) {
                                return (
                                    <div key={group} className="relative h-full">
                                        <RadioGroup value={currentGroup.label} id={group} className="peer sr-only" />
                                        <Label htmlFor={group} className="block h-full cursor-pointer">
                                            <Card
                                                className={cn(
                                                    'relative h-full transition-all duration-300',
                                                    'bg-white flex flex-col',
                                                    ` ${selectedSource === group ? 'border-purple-500 shadow-md' : ''}`,
                                                    'hover:border-purple-500 hover:shadow-md',
                                                    'peer-aria-checked:border-purple-600 peer-aria-checked:ring-2 peer-aria-checked:ring-purple-600/50',
                                                    'peer-aria-checked:shadow-lg peer-aria-checked:scale-[1.02] peer-aria-checked:z-10'
                                                )}
                                                onClick={() => {
                                                    if (!watch) return;
                                                    if (
                                                        group !== selectedSource &&
                                                        ((dataScience
                                                            ? watch('new_skill.config.columns_description')?.length
                                                            : watch('new_skill.config.columns_to_return')?.length) ??
                                                            0) > 0
                                                    ) {
                                                        setCurrentColumnsToReturn({
                                                            columnsToReturn: dataScience
                                                                ? watch('new_skill.config.columns_description')
                                                                : watch('new_skill.config.columns_to_return'),

                                                            group: selectedSource,
                                                        });
                                                        if (setValues) {
                                                            setValues('new_skill.config.columns_to_return', []);
                                                            setValues('new_skill.config.columns_description', []);
                                                        }
                                                    }

                                                    if (group === currentColumnsToReturn.group) {
                                                        if (setValues) {
                                                            dataScience
                                                                ? setValues(
                                                                      'new_skill.config.columns_description',
                                                                      currentColumnsToReturn.columnsToReturn
                                                                  )
                                                                : setValues(
                                                                      'new_skill.config.columns_to_return',
                                                                      currentColumnsToReturn.columnsToReturn
                                                                  );
                                                        }
                                                    }

                                                    setSelectedSource(group as EnumSkillRetrieverFields);
                                                }}
                                            >
                                                <CardHeader className="flex-grow relative border-l-4 pl-2 2xl:pl-6 border-transparent peer-aria-checked:border-purple-600">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className={cn(
                                                                    'p-2 rounded-lg transition-colors',
                                                                    'bg-purple-100 text-purple-600',
                                                                    'peer-aria-checked:bg-purple-600 peer-aria-checked:text-white'
                                                                )}
                                                            >
                                                                {currentGroup.icon && (
                                                                    <currentGroup.icon className="w-5 h-5" />
                                                                )}
                                                            </div>
                                                            <CardTitle className="text-lg">
                                                                {group.capitalize()}
                                                            </CardTitle>
                                                        </div>
                                                    </div>
                                                    <CardDescription>{currentGroup.description}</CardDescription>
                                                </CardHeader>
                                            </Card>
                                        </Label>
                                    </div>
                                );
                            }
                        })}
                    </RadioGroup>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selectedSource}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="rounded-lg border bg-white text-card-foreground shadow-sm p-6"
                        >
                            {selectedSource === EnumSkillRetrieverFields.EXCEL ? (
                                <div className={`grid grid-cols-1 gap-3 mb-3`}>
                                    {customRetrieverFields(name, selectedSource)}
                                </div>
                            ) : (
                                <>
                                    {selectedSource === EnumSkillRetrieverFields.REPORT &&
                                        watch &&
                                        watch('new_skill.config.report_key') && (
                                            <motion.div
                                                key='config_report_key'
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -20 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <div className="flex justify-end">
                                                    <Button
                                                        size={'sm'}
                                                        className="w-auto primary-gradient-button"
                                                        onClick={() =>
                                                            openNewWindownReport(
                                                                watch('new_skill.config.report_key') || ''
                                                            )
                                                        }
                                                    >
                                                        <Eye className="mr-2 h-4 w-4" />
                                                        {agent('view_report')}
                                                    </Button>
                                                </div>
                                            </motion.div>
                                        )}
                                    <div
                                        className={`grid ${skillRetrieverFields[selectedSource].columns ?? 'grid-cols-1 '} gap-3 mb-3`}
                                    >
                                        <RenderFormFieldsComponent
                                            fields={skillRetrieverFields[selectedSource].items}
                                            control={control}
                                        />
                                        {renderGroupFields()}
                                    </div>
                                </>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </>
    );
};

export default SkillRetrieverComponentDataSourceVersion;
